import { Observable, Subscription } from 'rxjs';
import { shareReplay, take, takeUntil } from 'rxjs/operators';

import { Action, ActionType } from '@ngxs/store';

import { clearStreamAction$ } from '@shared/decorators/clear-stream.decorator';
import { getActionTypeFromInstance } from '@ngxs/store/plugins';

export interface StreamActionOptions {
  /** When true, cancel ALL previous subscriptions for this handler when a new action is dispatched,
   *  not just the one with matching arguments. Use this for handlers that write to a single state
   *  slot regardless of action arguments (e.g. GetReports writing to `state.report.reports`). */
  exclusive?: boolean;
}

export function StreamAction(actions: ActionType | ActionType[], options?: StreamActionOptions) {
  return (target: any, name: string, descriptor: TypedPropertyDescriptor<any>) => {
    Action(actions)(target, name, descriptor);

    const originalMethod: () => any = descriptor.value;
    const calls: { [args: string]: Subscription } = {};

    descriptor.value = function (ctx, action) {
      let obs: Observable<any> = originalMethod.apply(this, [ctx, action]);

      if (!(obs instanceof Observable)) {
        return obs;
      }

      obs = obs.pipe(takeUntil(clearStreamAction$));

      if (options?.exclusive) {
        // Cancel all previous subscriptions for this handler to prevent
        // stale streams (e.g. from a different survey) from writing to the same state slot.
        for (const key of Object.keys(calls)) {
          calls[key].unsubscribe();
          delete calls[key];
        }
      }

      const args = JSON.stringify(action);

      if (calls[args]) {
        calls[args].unsubscribe();
      }

      calls[args] = obs.subscribe({
        error(error: { code: string }) {
          console.error(getActionTypeFromInstance(action), error);
        },
      });

      return obs.pipe(shareReplay({ refCount: true, bufferSize: 1 }), take(1));
    };

    return descriptor;
  };
}
